import { useState } from 'react';
import { usePhoneInput, defaultCountries, parseCountry } from 'react-international-phone';
import cn from 'classnames';
import { hasNationalPhoneDigits } from '@/shared/lib/utils/phoneNumber';
import { CustomSelect } from '@/shared/ui/Fields/CustomSelect';
import styles from './CustomPhoneInput.module.scss';

interface CustomPhoneInputProps {
  value: string;
  onChange: (phone: string) => void;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  showError?: boolean;
  isRequired?: boolean;
  defaultCountry?: string;
  className?: string;
}

const getFlagEmoji = (iso2: string) =>
  String.fromCodePoint(
    ...iso2
      .toUpperCase()
      .split('')
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );

const countryItems = defaultCountries.map((c) => {
  const { iso2, name, dialCode } = parseCountry(c);
  return { key: iso2, label: `${getFlagEmoji(iso2)} ${name} +${dialCode}` };
});

export const CustomPhoneInput = ({
  value,
  onChange,
  label,
  placeholder,
  errorMessage,
  showError = false,
  isRequired = false,
  defaultCountry = 'gb',
  className,
}: CustomPhoneInputProps) => {
  const isInvalid = showError && !!errorMessage;
  const [draft, setDraft] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const [pickedIso2, setPickedIso2] = useState(defaultCountry);

  if (value !== prevValue) {
    setPrevValue(value);
    if (value || !draft || draft === prevValue || hasNationalPhoneDigits(draft)) {
      setDraft(value);
    }
  }

  const { phone, handlePhoneValueChange, inputRef, country } = usePhoneInput({
    defaultCountry: pickedIso2,
    value: draft,
    countries: defaultCountries,
    onChange: ({ phone: nextPhone, country: nextCountry }) => {
      setDraft(nextPhone);
      if (nextCountry.iso2 !== pickedIso2) setPickedIso2(nextCountry.iso2);
      const next = hasNationalPhoneDigits(nextPhone, nextCountry.dialCode) ? nextPhone : '';
      if (next === value) return;
      onChange(next);
    },
  });

  const handleCountryChange = (keys: Iterable<unknown>) => {
    const nextIso = Array.from(keys)[0] as string | undefined;
    if (!nextIso || nextIso === pickedIso2) return;

    const next = defaultCountries.map(parseCountry).find((item) => item.iso2 === nextIso);
    if (!next) return;

    const digits = (draft || phone).replace(/\D/g, '');
    const national = digits.startsWith(country.dialCode)
      ? digits.slice(country.dialCode.length)
      : digits;
    const nextPhone = `+${next.dialCode}${national}`;

    setPickedIso2(nextIso);
    setDraft(nextPhone);
    const nextValue = national ? nextPhone : '';
    if (nextValue !== value) onChange(nextValue);
  };

  return (
    <div className={cn(styles.root, className)}>
      {label && (
        <label className={cn(styles.label, isInvalid && styles.labelError)}>
          {label}
          {isRequired && <span className={styles.required}> *</span>}
        </label>
      )}

      <div className={cn(styles.container, isInvalid && styles.containerError)}>
        <div className={styles.countryWrapper}>
          <CustomSelect
            style="black"
            hideLabel
            aria-label="Select country"
            items={countryItems}
            selectedKeys={new Set([pickedIso2])}
            onSelectionChange={handleCountryChange}
            renderValue={() => <span className={styles.flagEmoji}>{getFlagEmoji(pickedIso2)}</span>}
            popoverProps={{ style: { minWidth: '240px' } }}
          />
        </div>

        <div className={styles.divider} />

        <input
          ref={inputRef}
          value={phone}
          onChange={handlePhoneValueChange}
          placeholder={placeholder}
          className={styles.input}
          type="tel"
        />
      </div>

      {isInvalid && <p className={styles.errorMessage}>{errorMessage}</p>}
    </div>
  );
};
